import { useTranslation } from "react-i18next";

import type { AssetWeight } from "@/entities/robot";
import { formatPercent } from "@/shared/lib";
import { Panel } from "@/shared/ui";

/**
 * Рынки робота (ТЗ §9) — доли активов в работе стратегии.
 *
 * Доли приходят готовыми и в сумме дают ровно 100.00%: их раскладывает бэкенд
 * методом наибольших остатков. Складывать проценты на клиенте, чтобы дорисовать
 * «Other», запрещает правило ТЗ №5, да и остатка не бывает.
 *
 * Ширина полосы — от доли, поэтому она задаётся инлайном. Это единственное
 * место блока, где стиль вычисляется: цвет берётся от акцента робота токеном.
 */
export function MarketsBlock({ markets, color }: { markets: AssetWeight[]; color: string }) {
  const { t } = useTranslation();
  if (markets.length === 0) return null;

  return (
    <Panel as="section" radius="lg" className="flex flex-col gap-3 p-4">
      <header className="flex flex-col gap-0.5">
        <h2 className="text-body font-semibold text-text-primary">
          {t("robots.analytics.markets.title")}
        </h2>
        <p className="text-micro font-normal text-text-tertiary">
          {t("robots.analytics.markets.subtitle")}
        </p>
      </header>

      <ul className="flex flex-col gap-2.5">
        {markets.map((item) => (
          <li key={item.asset} className="flex min-w-0 flex-col gap-1">
            <div className="flex items-baseline justify-between gap-3 text-caption">
              <span className="truncate font-semibold text-text-primary">{item.asset}</span>
              <span className="shrink-0 font-medium text-text-secondary">
                {formatPercent(item.weight_pct)}
              </span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-background-raised">
              <div
                className="h-full rounded-full"
                style={{ width: `${item.weight_pct}%`, backgroundColor: color }}
              />
            </div>
          </li>
        ))}
      </ul>
    </Panel>
  );
}
